<script setup lang="ts">
import { Coupon, Item } from '@/@types/cart'
import Image from '@/assets/show/image.webp'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { useCartTotalsStore } from '@/composables/useCartTotalsStore'
import { currentPriceFormat, formatPrice } from '@/helpers/current-price'
import { findTicketLot } from '@/helpers/ticket-batch'
import { FormSchema } from '@/pages/carts/show.vue'
import { computed } from 'vue'
import Title from './Title.vue'

const props = defineProps<{ cartItems: Item[]; coupon?: Coupon; form: FormSchema }>()

const cartItems = computed(() => props.cartItems)
const coupon = computed(() => props.coupon)

const { standardCouponApplies, subtotal, total, installmentTotals, discount } = useCartTotalsStore()
</script>

<template>
    <section>
        <div class="mb-8">
            <Title :title="$t('Purchase details')" :description="$t('Confirm the information to make the payment')" />
        </div>
        <Card class="font-inter">
            <CardHeader>
                <CardTitle class="text-lg font-medium">
                    {{ $t('Order summary') }}
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div v-for="item in cartItems" :key="item.id" class="flex flex-wrap justify-between gap-4 border-b py-4 first:pt-0">
                    <div class="flex gap-4">
                        <img :src="Image" alt="Ingresso" class="h-[72px] w-16 rounded-md object-cover" />
                        <div class="flex w-full flex-col gap-1">
                            <p class="text-base font-normal">{{ $t('Ticket type') }}</p>
                            <p class="font-bold">{{ $t(item.ticket.category) }}</p>
                            <p class="text-sm">{{ $t('Batch') }} {{ findTicketLot(item.ticket.promotions) + 1 }}</p>
                        </div>
                    </div>

                    <p>{{ item.quantity }}x</p>

                    <p class="font-bold">{{ currentPriceFormat(item.ticket) }}</p>
                </div>
            </CardContent>
            <CardFooter class="flex flex-col gap-6">
                <div class="flex w-full flex-col gap-2 border-b pb-6">
                    <div class="flex w-full justify-between">
                        <p class="text-sm text-slate-600">{{ $t('Subtotal') }}:</p>
                        <p class="text-sm font-semibold text-slate-600">{{ formatPrice(subtotal) }}</p>
                    </div>
                    <div class="flex w-full justify-between" v-if="standardCouponApplies">
                        <p class="text-sm text-slate-600">{{ $t('Discounts') }}:</p>
                        <p class="text-sm font-semibold text-green-600">10% (-{{ formatPrice(discount) }})</p>
                    </div>

                    <div class="flex w-full justify-between" v-if="coupon">
                        <p class="text-sm text-slate-600">{{ $t('Coupon') }}:</p>
                        <p class="text-sm font-semibold text-green-600" v-if="coupon.category == 'currency'">
                            - {{ formatPrice(coupon.value_cents / 100) }}
                        </p>
                        <p class="text-sm font-semibold text-green-600" v-if="coupon.category == 'percentage' && !standardCouponApplies">
                            {{ coupon.percentage_value }}% (- {{ formatPrice(subtotal * (coupon.percentage_value / 100)) }})
                        </p>
                    </div>

                    <div v-if="form?.payment_type === 'credit_card'" class="flex flex-col gap-6">
                        <div class="flex justify-between text-sm">
                            <p class="font-semibold text-slate-800">{{ $t('You will pay') }}:</p>
                            <p class="font-semibold text-slate-800">
                                {{ form?.installments }}x {{ formatPrice((installmentTotals || total) / Number(form?.installments)) }}
                            </p>
                        </div>
                    </div>
                </div>
                <div class="flex w-full justify-between">
                    <p class="text-lg font-semibold">{{ $t('Total') }}:</p>
                    <p class="text-lg font-semibold">
                        {{ form?.payment_type === 'credit_card' && installmentTotals ? formatPrice(installmentTotals) : formatPrice(total) }}
                    </p>
                </div>
            </CardFooter>
        </Card>
    </section>
</template>
